import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, Clock, ShieldCheck, ChevronDown, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { CALENDLY_URL } from "@/lib/constants";
import LeadForm from "@/components/LeadForm";

const FAQs = [
  {
    q: "Who is this for?",
    a: "Black female entrepreneurs (and the women supporting them) — 35 to 55, $60K+, with a 9-to-5 and a vision to scale. If you're tired of the traditional 'business plan first' grind, you're home.",
  },
  {
    q: "What happens on the call?",
    a: "It's a candidate interview for School at Night. We assess where you are in your business and place you in the exact cohort that's gonna move the needle. No pitch, no pressure.",
  },
  {
    q: "Do I need to be technical?",
    a: "Not at all, sis. If you can type a text, you can prompt. We start from zero and walk you through every step.",
  },
  {
    q: "What if I can't make a session?",
    a: "Every cohort comes with recordings, the prompt library, and lifetime alumni access. Miss a week, catch up Sunday morning.",
  },
];

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-[#E5E5E5]" data-testid={`faq-${idx}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-toggle-${idx}`}
        className="w-full flex items-center justify-between text-left py-5"
      >
        <span className="font-display uppercase text-xl md:text-2xl tracking-tight pr-4">{q}</span>
        <ChevronDown
          className={`w-5 h-5 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="pb-6 text-black/70 leading-relaxed max-w-prose">{a}</p>
      )}
    </div>
  );
}

export default function BookNow() {
  const [step, setStep] = useState("form"); // "form" | "calendar"

  return (
    <div data-testid="book-page">
      {/* HEADER */}
      <section
        className="relative bg-[#0A0A0A] text-white overflow-hidden grain"
        data-testid="book-header"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-20">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFD60A] font-bold mb-6 fade-up">
            School at Night · Candidate Interview
          </p>
          <h1 className="font-display uppercase leading-[0.88] text-6xl md:text-8xl lg:text-9xl fade-up delay-1">
            Let's get <span className="hi-yellow">to work.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-lg md:text-xl leading-relaxed fade-up delay-2">
            Pick a time that works for your real life. 20 minutes, one-on-one
            with the team. We'll find the cohort that matches where you are —
            and where you're going.
          </p>
        </div>
      </section>

      {/* BOOKING AREA */}
      <section className="bg-[#FAFAFA] py-16 md:py-24" data-testid="book-booking-area">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* LEFT — TRUST */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <p className="text-[11px] uppercase tracking-[0.3em] text-black/50 font-bold mb-4">
              What to expect
            </p>
            <h2 className="font-display uppercase text-4xl md:text-5xl leading-[0.95]">
              20 minutes. <br/> Zero pressure.
            </h2>

            <ul className="mt-8 space-y-5">
              {[
                { Icon: Calendar, t: "Real human on the call", s: "Not a sales bot. You'll talk to the team that built the curriculum." },
                { Icon: Clock, t: "20 minutes, your pace", s: "Quick, focused, and respectful of your time." },
                { Icon: ShieldCheck, t: "No pitch slap", s: "If we're not the right fit, we'll tell you — and point you somewhere better." },
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="inline-flex w-10 h-10 bg-black text-[#FFD60A] items-center justify-center flex-shrink-0">
                    <b.Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <p className="font-bold">{b.t}</p>
                    <p className="text-sm text-black/65 leading-relaxed mt-1">{b.s}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-10 p-6 bg-black text-white">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFD60A] font-bold mb-3">
                Prefer to message?
              </p>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                Slide in our DMs — we keep it real and respond fast.
              </p>
              <a
                href="https://instagram.com/keziamw"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-instagram-cta"
                className="inline-flex items-center gap-2 bg-[#FFD60A] text-black px-5 py-3 font-bold uppercase tracking-wider text-[12px] hover:bg-white transition-colors"
              >
                DM @keziamw <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </aside>

          {/* RIGHT — STEP 1: LEAD FORM or STEP 2: CALENDLY EMBED */}
          <div className="lg:col-span-8">
            {step === "form" ? (
              <LeadForm onSuccess={() => setStep("calendar")} />
            ) : (
              <>
                <button
                  onClick={() => setStep("form")}
                  data-testid="back-to-form"
                  className="mb-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold text-black/55 hover:text-black"
                >
                  <ArrowLeft className="w-4 h-4" /> Edit my info
                </button>
                <div className="bg-white border border-[#E5E5E5] shadow-hard-black p-2 md:p-3" data-testid="calendly-frame">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E5E5]">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-[#FFD60A]" />
                      <p className="text-[11px] uppercase tracking-[0.3em] font-bold">
                        calendly · professorkez
                      </p>
                    </div>
                    <a
                      href={CALENDLY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="calendly-open-new-tab"
                      className="text-[11px] uppercase tracking-[0.25em] font-bold hover:text-black/60"
                    >
                      Open in new tab ↗
                    </a>
                  </div>
                  <iframe
                    src={CALENDLY_URL}
                    title="Book a candidate interview with Professor Kez"
                    className="w-full h-[720px] md:h-[820px] border-0 bg-white"
                    data-testid="calendly-iframe"
                    loading="lazy"
                  />
                </div>

                <p className="text-xs text-black/50 mt-3 text-center">
                  Booking powered by Calendly. Your info stays with the SkillhouseU
                  team — we don't sell data, period.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#FAFAFA] pb-24 md:pb-32" data-testid="book-faq-section">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-10">
            <div className="md:col-span-5">
              <p className="text-[11px] uppercase tracking-[0.3em] text-black/50 font-bold mb-3">
                FAQ
              </p>
              <h2 className="font-display uppercase text-5xl md:text-6xl leading-[0.95]">
                Questions, <br/> we got answers.
              </h2>
            </div>
            <div className="md:col-span-7">
              {FAQs.map((f, i) => (
                <FAQItem key={f.q} q={f.q} a={f.a} idx={i} />
              ))}
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/services"
              data-testid="book-services-cta"
              className="inline-flex items-center gap-2 bg-black text-[#FFD60A] px-7 py-5 font-bold uppercase tracking-[0.2em] text-sm hover:bg-[#FFD60A] hover:text-black transition-colors"
            >
              Browse Courses Instead <ArrowUpRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
